import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { RefObject, useCallback, useEffect, useState } from 'react';

type ScrollArrowsProps = Readonly<{
  scrollRef: RefObject<HTMLDivElement | null>;
}>;

export default function ScrollArrows({ scrollRef }: ScrollArrowsProps) {
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [hasOverflow, setHasOverflow] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const overflow = el.scrollWidth > el.clientWidth + 1;
    setHasOverflow(overflow);
    setCanScrollLeft(el.scrollLeft > 1);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, [scrollRef]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;

    updateScrollState();

    el.addEventListener('scroll', updateScrollState, { passive: true });

    const observer = new ResizeObserver(updateScrollState);
    observer.observe(el);

    return () => {
      el.removeEventListener('scroll', updateScrollState);
      observer.disconnect();
    };
  }, [scrollRef, updateScrollState]);

  const scrollByColumn = (direction: 1 | -1) => {
    const el = scrollRef.current;
    if (!el) return;
    const firstChild = el.children[0] as HTMLElement | undefined;
    if (!firstChild) return;
    const gap = parseFloat(getComputedStyle(el).gap) || 0;
    const columnWidth = firstChild.getBoundingClientRect().width + gap;
    el.scrollBy({ left: direction * columnWidth, behavior: 'smooth' });
  };

  if (!hasOverflow) return null;

  return (
    <>
      <button
        type='button'
        disabled={!canScrollLeft}
        onClick={() => scrollByColumn(-1)}
        className='absolute left-0 top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-white shadow-lg ring-1 ring-black/10 transition-all hover:scale-110 hover:text-gray-700 disabled:cursor-default disabled:opacity-0'
      >
        <ChevronLeftIcon className='h-5 w-5' />
      </button>
      <button
        type='button'
        disabled={!canScrollRight}
        onClick={() => scrollByColumn(1)}
        className='absolute right-0 top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-white shadow-lg ring-1 ring-black/10 transition-all hover:scale-110 hover:text-gray-700 disabled:cursor-default disabled:opacity-0'
      >
        <ChevronRightIcon className='h-5 w-5' />
      </button>
    </>
  );
}
